import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import AccessibleTable from './AccessibleTable';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthService from '../services/AuthService';
import { AccessUser, GraduationApi, errorMessage } from '../services/GraduationApi';
import { ImpersonationMode, ImpersonationStore, Student } from '../services/ImpersonationStore';
import { useImpersonation } from './StudentWorkspace';

type ImpersonationType = 'Student' | 'Evaluator' | 'Viewer' | 'GraduateEvaluator' | 'GraduateFaculty';

const details: Record<ImpersonationType, { person: string; plural: string; role?: string }> = {
  Student: { person: 'student', plural: 'students' },
  Evaluator: { person: 'evaluator', plural: 'undergraduate evaluators', role: 'undergraduateevaluator' },
  Viewer: { person: 'viewer', plural: 'undergraduate viewers', role: 'undergraduateviewer' },
  GraduateEvaluator: { person: 'graduate evaluator', plural: 'graduate evaluators', role: 'graduateevaluator' },
  GraduateFaculty: { person: 'graduate faculty member', plural: 'graduate faculty', role: 'graduatefaculty' },
};

export default function Impersonate() {
  const [type, setType] = useState<ImpersonationType>('Student');
  const [identifier, setIdentifier] = useState('');
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<AccessUser[]>([]);
  const [person, setPerson] = useState<Student | null>(null);
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<ImpersonationMode>('View');
  const [error, setError] = useState('');
  const [listError, setListError] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const pending = useRef<AbortController | null>(null);
  const confirmation = useRef<HTMLHeadingElement>(null);
  const navigate = useNavigate();
  const session = useImpersonation();
  const account = AuthService.getCurrentToken();
  const allowed = Boolean(account?.canImpersonate && !session);
  const detail = details[type];

  useEffect(() => {
    if (!allowed || type === 'Student' || type === 'GraduateFaculty') return;
    const controller = new AbortController();
    setLoading(true); setListError('');
    GraduationApi.impersonationCandidates(type, controller.signal)
      .then(result => {
        if (!controller.signal.aborted) setUsers(result
          .filter(user => user.roleNames.some(role => role.toLowerCase() === details[type].role))
          .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }) ||
            a.starId.localeCompare(b.starId)));
      })
      .catch(e => { if (!controller.signal.aborted && !axios.isCancel(e)) setListError(errorMessage(e)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [allowed, type, revision]);
  useEffect(() => {
    if (!allowed || type !== 'GraduateFaculty') return;
    const query = search.trim();
    setUsers([]); setListError('');
    if (query.length < 2) { setLoading(false); return; }
    const controller = new AbortController();
    setLoading(true);
    const timer = window.setTimeout(() => {
      GraduationApi.searchGraduateFaculty(query, controller.signal)
        .then(result => { if (!controller.signal.aborted) setUsers(result.map(person => ({ ...person, roleNames: ['GraduateFaculty'] }))); })
        .catch(e => { if (!controller.signal.aborted && !axios.isCancel(e)) setListError(errorMessage(e)); })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 300);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [allowed, type, search, revision]);
  useEffect(() => () => pending.current?.abort(), []);
  useEffect(() => { if (person) confirmation.current?.focus(); }, [person]);

  const visibleUsers = useMemo(() => {
    if (type === 'GraduateFaculty') return users;
    const term = search.trim().toLocaleLowerCase();
    return users.filter(user => !term || [user.displayName, user.starId, user.techId]
      .some(value => (value || '').toLocaleLowerCase().includes(term)));
  }, [users, search, type]);

  const changeType = (next: ImpersonationType) => {
    pending.current?.abort();
    setType(next); setIdentifier(''); setSearch(''); setUsers([]); setPerson(null);
    setReason(''); setMode('View'); setError(''); setListError(''); setBusy(false);
  };

  const lookupStudent = async (event: React.FormEvent) => {
    event.preventDefault(); pending.current?.abort();
    const controller = new AbortController(); pending.current = controller;
    setBusy(true); setError(''); setPerson(null); setReason(''); setMode('View');
    try {
      const found = await GraduationApi.lookup(identifier, controller.signal);
      if (!controller.signal.aborted) setPerson(found);
    } catch (e) { if (!controller.signal.aborted && !axios.isCancel(e)) setError(errorMessage(e)); }
    finally { if (pending.current === controller) setBusy(false); }
  };

  const select = async (starId: string) => {
    pending.current?.abort();
    const controller = new AbortController(); pending.current = controller;
    setBusy(true); setError(''); setPerson(null); setReason(''); setMode('View');
    try {
      const found = type === 'Evaluator' ? await GraduationApi.lookupEvaluator(starId, controller.signal) :
        type === 'Viewer' ? await GraduationApi.lookupViewer(starId, controller.signal) :
        type === 'GraduateEvaluator' ? await GraduationApi.lookupGraduateEvaluator(starId, controller.signal) :
        await GraduationApi.lookupGraduateFaculty(starId, controller.signal);
      if (!controller.signal.aborted) setPerson(found);
    } catch (e) { if (!controller.signal.aborted && !axios.isCancel(e)) setError(errorMessage(e)); }
    finally { if (pending.current === controller) setBusy(false); }
  };

  const start = async () => {
    if (!person || busy || reason.trim().length < 3) return;
    setBusy(true); setError('');
    try {
      const result = type === 'Student'
        ? await GraduationApi.start(person.starId, reason.trim(), mode)
        : type === 'Evaluator'
          ? await GraduationApi.startEvaluator(person.starId, reason.trim(), mode)
          : type === 'Viewer' ? await GraduationApi.startViewer(person.starId, reason.trim())
          : type === 'GraduateEvaluator' ? await GraduationApi.startGraduateEvaluator(person.starId, reason.trim(), mode)
          : await GraduationApi.startGraduateFaculty(person.starId, reason.trim(), mode);
      ImpersonationStore.setStarted(result);
      navigate(type === 'Student' ? '/' :
        type === 'GraduateEvaluator' || type === 'GraduateFaculty' ? '/admin/graduate' : '/admin/undergraduate', { replace: true });
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };

  if (!account?.canImpersonate) return <Alert variant="danger">Administrator access is required.</Alert>;
  if (session) return <p>Stop the current impersonation session before selecting another user.</p>;
  return <section aria-labelledby="impersonation-heading">
    <h1 id="impersonation-heading">Impersonate User</h1>
    <p>Choose who you need to impersonate, confirm their identity, and enter a reason. Your administrator identity and reason are recorded.</p>
    <Form.Group controlId="impersonation-type" className="section-panel mb-4" style={{ maxWidth: 560 }}>
      <Form.Label>Impersonation type</Form.Label>
      <Form.Select value={type} disabled={busy} onChange={e => changeType(e.target.value as ImpersonationType)}>
        <option value="Student">Student</option>
        {(account?.isUndergraduateAdministrator ?? account?.canImpersonate) && <option value="Evaluator">Undergraduate evaluator</option>}
        {(account?.isUndergraduateAdministrator ?? account?.canImpersonate) && <option value="Viewer">Undergraduate viewer</option>}
        {(account?.isGraduateAdministrator ?? account?.canImpersonate) && <option value="GraduateEvaluator">Graduate evaluator</option>}
        {(account?.isGraduateAdministrator ?? account?.canImpersonate) && <option value="GraduateFaculty">Graduate faculty</option>}
      </Form.Select>
    </Form.Group>
    {error && <Alert variant="danger" role="alert">{error}</Alert>}
    {type === 'Student' ? <Form onSubmit={lookupStudent} className="section-panel mb-4" style={{ maxWidth: 560 }}>
      <Form.Group controlId="student-identifier" className="mb-3">
        <Form.Label>Student Tech ID or StarID</Form.Label>
        <Form.Control value={identifier} required maxLength={8} autoComplete="off" disabled={busy}
          pattern="([0-9]{8}|[a-zA-Z]{2}[0-9]{4}[a-zA-Z]{2})"
          aria-describedby="student-identifier-help" onChange={e => { pending.current?.abort(); setIdentifier(e.target.value); setPerson(null); setReason(''); setMode('View'); setError(''); }} />
        <Form.Text id="student-identifier-help">Enter an eight-digit Tech ID or a StarID such as ab1234cd.</Form.Text>
      </Form.Group>
      <Button type="submit" disabled={busy}>{busy ? 'Working…' : 'Find student'}</Button>
    </Form> : <section className="section-panel mb-4" aria-labelledby="user-list-heading">
      <h2 id="user-list-heading" className="h4">Available {detail.plural}</h2>
      <Form.Group controlId="user-search" className="mb-3">
        <Form.Label>Search {detail.plural} by name, StarID, or Tech ID</Form.Label>
        <Form.Control value={search} maxLength={100} autoComplete="off" disabled={busy || (type !== 'GraduateFaculty' && loading)}
          onChange={e => { pending.current?.abort(); setSearch(e.target.value); setPerson(null); setReason(''); setMode('View'); setError(''); }} />
      </Form.Group>
      {listError && <Alert variant="danger" role="alert">{detail.plural} could not be loaded. {listError} <Button variant="outline-danger" size="sm" onClick={() => setRevision(value => value + 1)}>Try again</Button></Alert>}
      {loading && <p role="status">Loading {detail.plural}…</p>}
      {!loading && !listError && (type === 'GraduateFaculty' && search.trim().length < 2
        ? <p>Enter at least two characters to search the Graduate Faculty roster.</p>
        : visibleUsers.length ? <AccessibleTable scrollLabel="Users available for impersonation" striped hover>
        <thead><tr><th scope="col">Name</th><th scope="col">StarID</th><th scope="col">Tech ID</th><th scope="col">Action</th></tr></thead>
        <tbody>{visibleUsers.map(user => <tr key={user.starId}><th scope="row">{user.displayName || user.starId}</th>
          <td>{user.starId}</td><td>{user.techId || 'Unavailable'}</td>
          <td>{user.starId.toLowerCase() === account.starId?.toLowerCase() ? 'Your account' :
            <Button type="button" variant="outline-primary" size="sm" disabled={busy}
              aria-label={`Select ${user.displayName || user.starId} (${user.starId})`} onClick={() => select(user.starId)}>Select</Button>}</td>
        </tr>)}</tbody></AccessibleTable> : <p>{type === 'GraduateFaculty' || users.length ? `No ${detail.plural} match this search.` : `No active ${detail.plural} are available.`}</p>)}
    </section>}
    {person && <section className="section-panel" style={{ maxWidth: 640 }} aria-labelledby="confirm-person-heading">
      <h2 id="confirm-person-heading" ref={confirmation} tabIndex={-1}>Confirm {detail.person}</h2>
      <dl className="data-summary"><dt>Name</dt><dd>{person.displayName}</dd><dt>Tech ID</dt><dd>{person.techId}</dd><dt>StarID</dt><dd>{person.starId}</dd></dl>
      <Form.Group controlId="impersonation-reason" className="mb-3">
        <Form.Label>Reason for impersonating this {detail.person}</Form.Label>
        <Form.Control as="textarea" rows={2} required minLength={3} maxLength={500} value={reason} disabled={busy}
          aria-describedby="impersonation-reason-help" onChange={e => setReason(e.target.value)} />
        <Form.Text id="impersonation-reason-help">Required. Enter at least 3 characters, excluding spaces at the beginning and end. The reason and your administrator identity are recorded for auditing.</Form.Text>
      </Form.Group>
      {type !== 'Viewer' && <fieldset className="mb-3" disabled={busy}>
        <legend className="h5">Impersonation mode</legend>
        <Form.Check id="impersonation-view" type="radio" name="impersonation-mode" label="View only" checked={mode === 'View'} onChange={() => setMode('View')} />
        <Form.Check id="impersonation-edit" type="radio" name="impersonation-mode"
          label={type === 'Student' ? 'Edit — create and edit applications' :
            type === 'GraduateEvaluator' ? 'Edit graduate applications' : type === 'GraduateFaculty' ?
            'Approve assigned graduate applications' : 'Edit undergraduate applications'}
          checked={mode === 'Edit'} onChange={() => setMode('Edit')} />
      </fieldset>}
      {mode === 'Edit' && <Alert variant="warning">Changes to applications will be audited with your administrator identity and the {detail.person}’s identity.</Alert>}
      <Button onClick={start} disabled={busy || reason.trim().length < 3}>{mode === 'Edit' ? 'Start Edit impersonation' : 'Start read-only impersonation'}</Button>
    </section>}
  </section>;
}
